// Rasterise `EditedImageBundle` + source blob to JPEG bytes for upload.

import type {
  DrawColor,
  DrawStroke,
  EditedImageBundle,
  ImageEditOperation,
  ImageSource,
  TextOverlay,
  TextOverlayColor,
} from '@ping/ui';

const MAX_EDGE_PX = 2048;
const JPEG_QUALITY = 0.9;

const DRAW_COLOR_HEX: Readonly<Record<DrawColor, string>> = {
  white: '#FFFFFF',
  black: '#000000',
  rose: '#D94873',
  mint: '#36A67E',
  amber: '#D67D2F',
  indigo: '#5C7BF0',
};

const TEXT_COLOR_HEX: Readonly<Record<TextOverlayColor, string>> = {
  white: '#FFFFFF',
  black: '#000000',
  accent: '#5C7BF0',
  rose: '#D94873',
  mint: '#36A67E',
  amber: '#D67D2F',
};

const BRUSH_WIDTH_PX: Readonly<Record<DrawStroke['brushSize'], number>> = {
  fine: 3,
  medium: 8,
  bold: 14,
};

export interface ExportedImage {
  readonly bytes: Uint8Array;
  readonly mime: 'image/jpeg';
  readonly width: number;
  readonly height: number;
}

export async function exportEditedImage(
  bundle: EditedImageBundle,
  source: ImageSource,
): Promise<ExportedImage> {
  const img = await loadImageFromBlob(source.blob);
  const { rotationQuarterTurns, rectNormalised } = deriveCropTransform(bundle.operations);
  const extraRotation = ((bundle.orientationQuarterTurns % 4) + 4) % 4;
  const totalRotation = (rotationQuarterTurns + extraRotation) % 4;

  const srcW = img.naturalWidth;
  const srcH = img.naturalHeight;
  const cropX = Math.round(rectNormalised.x * srcW);
  const cropY = Math.round(rectNormalised.y * srcH);
  const cropW = Math.max(1, Math.round(rectNormalised.w * srcW));
  const cropH = Math.max(1, Math.round(rectNormalised.h * srcH));

  let outW = totalRotation % 2 === 0 ? cropW : cropH;
  let outH = totalRotation % 2 === 0 ? cropH : cropW;
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(outW, outH));
  outW = Math.max(1, Math.round(outW * scale));
  outH = Math.max(1, Math.round(outH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    throw new Error('exportEditedImage: canvas 2d unavailable');
  }

  ctx.save();
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate((totalRotation * Math.PI) / 2);
  const drawW = totalRotation % 2 === 0 ? outW : outH;
  const drawH = totalRotation % 2 === 0 ? outH : outW;
  ctx.drawImage(img, cropX, cropY, cropW, cropH, -drawW / 2, -drawH / 2, drawW, drawH);
  ctx.restore();

  const strokes = deriveCommittedStrokes(bundle.operations);
  for (const stroke of strokes) {
    drawStroke(ctx, stroke, outW, outH);
  }

  const overlays = deriveTextOverlays(bundle.operations);
  for (const overlay of overlays) {
    drawTextOverlay(ctx, overlay, outW, outH);
  }

  const blob = await canvasToJpegBlob(canvas);
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return { bytes, mime: 'image/jpeg', width: outW, height: outH };
}

function deriveCropTransform(operations: ReadonlyArray<ImageEditOperation>): {
  rotationQuarterTurns: number;
  rectNormalised: { x: number; y: number; w: number; h: number };
} {
  let rotationQuarterTurns = 0;
  let cropRect = { x: 0, y: 0, w: 1, h: 1 };
  for (const op of operations) {
    if (op.kind === 'crop') {
      cropRect = {
        x: op.rectNormalised.xNormalised,
        y: op.rectNormalised.yNormalised,
        w: op.rectNormalised.widthNormalised,
        h: op.rectNormalised.heightNormalised,
      };
      rotationQuarterTurns = op.rotationQuarterTurns;
    } else if (op.kind === 'rotate') {
      rotationQuarterTurns = (rotationQuarterTurns + op.quarterTurns) % 4;
    }
  }
  return { rotationQuarterTurns, rectNormalised: cropRect };
}

function deriveCommittedStrokes(operations: ReadonlyArray<ImageEditOperation>): ReadonlyArray<DrawStroke> {
  const out: DrawStroke[] = [];
  for (const op of operations) {
    if (op.kind === 'drawStroke') {
      out.push(op.stroke);
    }
  }
  return out;
}

function deriveTextOverlays(operations: ReadonlyArray<ImageEditOperation>): ReadonlyArray<TextOverlay> {
  const live = new Map<string, TextOverlay>();
  for (const op of operations) {
    if (op.kind === 'addText') {
      live.set(op.overlay.id, op.overlay);
    } else if (op.kind === 'moveText') {
      const existing = live.get(op.id);
      if (existing) {
        live.set(op.id, { ...existing, positionNormalised: op.toNormalised });
      }
    } else if (op.kind === 'deleteText') {
      live.delete(op.id);
    }
  }
  return Array.from(live.values());
}

function drawStroke(
  ctx: CanvasRenderingContext2D,
  stroke: DrawStroke,
  width: number,
  height: number,
): void {
  const verts = stroke.verticesNormalised;
  if (verts.length === 0) return;
  ctx.save();
  ctx.strokeStyle = DRAW_COLOR_HEX[stroke.color];
  ctx.lineWidth = BRUSH_WIDTH_PX[stroke.brushSize];
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  const first = verts[0];
  if (first) {
    ctx.moveTo(first[0] * width, first[1] * height);
  }
  for (let i = 1; i < verts.length; i++) {
    const v = verts[i];
    if (v) ctx.lineTo(v[0] * width, v[1] * height);
  }
  ctx.stroke();
  ctx.restore();
}

function drawTextOverlay(
  ctx: CanvasRenderingContext2D,
  overlay: TextOverlay,
  width: number,
  height: number,
): void {
  const x = overlay.positionNormalised.xNormalised * width;
  const y = overlay.positionNormalised.yNormalised * height;
  const fontSize = Math.max(12, overlay.fontSize);
  ctx.save();
  ctx.font = `600 ${fontSize}px system-ui, sans-serif`;
  ctx.fillStyle = TEXT_COLOR_HEX[overlay.color];
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(overlay.text, x, y);
  ctx.restore();
}

function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('exportEditedImage: decode failed'));
    };
    img.src = url;
  });
}

function canvasToJpegBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('exportEditedImage: toBlob failed'));
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}
